import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import { GeneralRoom, UserSupportPanel, AdminSupportInbox } from './ChatCore';
import api from '../../utils/api';

const ADMIN_ROLES = ['super_admin', 'manager', 'assistant'];

export default function ChatBubble() {
  const { user } = useAuth();
  const { socket, notification, onlineUsers } = useSocket();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('general');
  const [unread, setUnread] = useState(0);
  const isAdmin = ADMIN_ROLES.includes(user?.role);

  // Load unread count
  useEffect(() => {
    if (!user) return;
    api.get('/chat/unread').then(({ data }) => setUnread(data.count || 0)).catch(() => {});
  }, [user, open]);

  // Reset unread when opened
  useEffect(() => { if (open) setUnread(0); }, [open]);

  // Socket: track new private messages for unread badge
  useEffect(() => {
    if (!socket) return;
    const handler = () => { if (!open) setUnread(n => n + 1); };
    socket.on('new_private_message', handler);
    socket.on('new_general_message', handler);
    return () => { socket.off('new_private_message', handler); socket.off('new_general_message', handler); };
  }, [socket, open]);

  if (!user) return null;

  return (
    <>
      {/* Notification toast */}
      {notification && !open && (
        <div onClick={() => { setOpen(true); setTab('support'); }}
          style={{ position: 'fixed', bottom: 92, right: 24, background: 'var(--bg-card)', border: '1px solid #f5c400', borderRadius: 12, padding: '12px 16px', zIndex: 9998, cursor: 'pointer', maxWidth: 260, boxShadow: '0 4px 20px rgba(245,196,0,0.25)', animation: 'fadeIn 0.3s ease' }}>
          <div style={{ fontSize: 12, color: '#f5c400', fontWeight: 700, marginBottom: 4 }}>💬 {notification.from?.username}</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{notification.preview}</div>
        </div>
      )}

      {/* Floating button */}
      <button onClick={() => setOpen(!open)}
        style={{ position: 'fixed', bottom: 24, right: 24, width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg,#f5c400,#c9a000)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, zIndex: 9999, boxShadow: '0 4px 20px rgba(245,196,0,0.4)', transition: 'transform 0.2s' }}
        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
        {open ? '✕' : '💬'}
        {unread > 0 && !open && (
          <div style={{ position: 'absolute', top: -4, right: -4, background: '#ef4444', color: '#fff', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 900 }}>
            {unread > 9 ? '9+' : unread}
          </div>
        )}
      </button>

      {/* Chat window — same structure as full page */}
      {open && (
        <div style={{ position: 'fixed', bottom: 92, right: 24, width: 360, height: 520, background: 'var(--bg-card)', border: '1px solid var(--border-glow)', borderRadius: 16, boxShadow: '0 8px 40px rgba(245,196,0,0.2)', zIndex: 9998, display: 'flex', flexDirection: 'column', overflow: 'hidden', animation: 'fadeIn 0.2s ease' }}>

          {/* Header — same style as Chat page */}
          <div style={{ background: 'linear-gradient(135deg,#f5c400,#c9a000)', padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 16 }}>💬</span>
              <span style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 700, color: '#0a0900' }}>LIVE CHAT</span>
              <span style={{ fontSize: 11, color: '#0a0900', opacity: 0.6 }}>· {onlineUsers.length} online</span>
            </div>
            <Link to="/chat" onClick={() => setOpen(false)} style={{ fontSize: 11, color: '#0a0900', opacity: 0.7, fontWeight: 600, textDecoration: 'none' }}>Full View ↗</Link>
          </div>

          {/* Tabs — identical to Chat page */}
          <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
            <button onClick={() => setTab('general')} style={{ flex: 1, padding: '8px', background: tab === 'general' ? 'rgba(245,196,0,0.08)' : 'transparent', border: 'none', borderBottom: tab === 'general' ? '2px solid #f5c400' : '2px solid transparent', color: tab === 'general' ? '#f5c400' : 'var(--text-muted)', fontWeight: 700, fontSize: 12, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              🌐 General
            </button>
            <button onClick={() => setTab('support')} style={{ flex: 1, padding: '8px', background: tab === 'support' ? 'rgba(245,196,0,0.08)' : 'transparent', border: 'none', borderBottom: tab === 'support' ? '2px solid #f5c400' : '2px solid transparent', color: tab === 'support' ? '#f5c400' : 'var(--text-muted)', fontWeight: 700, fontSize: 12, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {isAdmin ? '📬 Inbox' : '🎧 Support'}
            </button>
          </div>

          {/* Content — uses exact same components as full page */}
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {tab === 'general'
              ? <GeneralRoom socket={socket} currentUser={user} compact={true} />
              : isAdmin
                ? <AdminSupportInbox socket={socket} currentUser={user} />
                : <UserSupportPanel socket={socket} currentUser={user} compact={true} />
            }
          </div>
        </div>
      )}
    </>
  );
}
